/**
 * User identity and attributes.
 *
 * - identifyUser(id): the MOBILE NUMBER is the MoEngage unique id, set at
 *   mobile OTP verified (same as the app — docs/moengage_events_handover.md §4).
 *   It is never switched later: changing a user's unique id splits or merges
 *   profiles in MoEngage. ekyc_user_id / client_id are user attributes.
 * - setUserProfile(): standard MoEngage fields + custom attributes
 *   mobile_number, ekyc_user_id, customer_status, lead_filled_date,
 *   client_name, client_id, dob (string dd/MM/yyyy). Values are also
 *   remembered so later events can auto-fill mobile_number / email_id /
 *   full_name / ekyc_user_id / Client_Code.
 * - logoutUser(): on logout / journey reset.
 *
 * All functions are fire-and-forget and never throw.
 */
import { config } from './config';
import { enqueue } from './moengageClient';
import { forgetUser, rememberEsign, rememberUser } from './context';
import { resetCommonProps } from './commonProps';
import { resetDeferredEvents, resetTrackOnce } from './track';
import { debugInfo, debugWarn, maskProps } from './logger';

let lastIdentified = null;

const pad = (n) => String(n).padStart(2, '0');

/**
 * Normalise a date to the string dd/MM/yyyy (the format the app sends for
 * `dob` / `lead_filled_date`). Accepts Date, ISO strings, and the DD/MM/YYYY
 * or DD-MM-YYYY formats the PAN APIs return. Unknown → undefined.
 * Never a Date object or ISO string — that would not match the app.
 */
export const toDdMmYyyy = (value) => {
    if (value == null || value === '') return undefined;
    if (Object.prototype.toString.call(value) === '[object Date]') {
        return Number.isNaN(value.getTime())
            ? undefined
            : `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}`;
    }
    const s = String(value).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) return `${pad(m[1])}/${pad(m[2])}/${m[3]}`;
    return undefined;
};

/**
 * Set the MoEngage unique id. Repeated calls with the same id are ignored.
 * @param {string|number} uniqueId
 * @returns {void}
 */
export const identifyUser = (uniqueId) => {
    try {
        if (!config.enabled || uniqueId == null || uniqueId === '') return;
        const id = String(uniqueId);
        if (id === lastIdentified) return;
        lastIdentified = id;
        debugInfo(config.mockMode ? '[MoEngage:mock] identifyUser' : '[MoEngage] identifyUser', id);
        enqueue((sdk) => sdk.identifyUser(id));
    } catch (e) {
        debugWarn('[MoEngage] identifyUser failed', e);
    }
};

/**
 * @param {{ mobile?: string, email?: string, fullName?: string, firstName?: string, lastName?: string,
 *           ekycUserId?: string|number, customerStatus?: string, leadFilledDate?: string|Date,
 *           clientName?: string, clientId?: string, dob?: string|Date }} profile
 * @returns {void}
 */
export const setUserProfile = (profile = {}) => {
    try {
        if (!config.enabled || !profile) return;
        const dob = toDdMmYyyy(profile.dob);
        const leadFilledDate = toDdMmYyyy(profile.leadFilledDate);
        rememberUser({
            mobile: profile.mobile,
            email: profile.email,
            fullName: profile.fullName,
            ekycUserId: profile.ekycUserId,
            clientName: profile.clientName,
            clientId: profile.clientId,
            dob,
        });

        const has = (v) => v != null && String(v).trim() !== '';
        const attr = (key, value) => (sdk) => sdk.setUserAttribute(key, String(value));
        const calls = [];
        if (has(profile.mobile)) {
            calls.push((sdk) => sdk.setMobileNumber(String(profile.mobile)));
            calls.push(attr('mobile_number', profile.mobile));
        }
        if (has(profile.email)) calls.push((sdk) => sdk.setEmailId(String(profile.email)));
        if (has(profile.fullName)) calls.push((sdk) => sdk.setUserName(String(profile.fullName)));
        if (has(profile.firstName)) calls.push((sdk) => sdk.setFirstName(String(profile.firstName)));
        if (has(profile.lastName)) calls.push((sdk) => sdk.setLastName(String(profile.lastName)));
        if (has(profile.ekycUserId)) calls.push(attr('ekyc_user_id', profile.ekycUserId));
        if (has(profile.customerStatus)) calls.push(attr('customer_status', profile.customerStatus));
        if (leadFilledDate) calls.push(attr('lead_filled_date', leadFilledDate));
        if (has(profile.clientName)) calls.push(attr('client_name', profile.clientName));
        if (has(profile.clientId)) calls.push(attr('client_id', profile.clientId));
        if (dob) calls.push(attr('dob', dob));
        if (!calls.length) return;

        debugInfo(config.mockMode ? '[MoEngage:mock] setUserProfile' : '[MoEngage] setUserProfile', maskProps({
            mobile_number: profile.mobile,
            email_id: profile.email,
            user_name: profile.fullName,
            ekyc_user_id: profile.ekycUserId,
            customer_status: profile.customerStatus,
            lead_filled_date: leadFilledDate,
            client_name: profile.clientName,
            client_id: profile.clientId,
            dob,
        }));
        calls.forEach((call) => enqueue(call));
    } catch (e) {
        debugWarn('[MoEngage] setUserProfile failed', e);
    }
};

/**
 * Remember user values for event auto-fill WITHOUT sending anything to
 * MoEngage. Use when a value is known early but the sheet sets the user
 * attribute later).
 * @param {{ mobile?: string, email?: string, fullName?: string, ekycUserId?: string, clientName?: string,
 *           clientId?: string, dob?: string|Date }} details
 * @returns {void}
 */
export const rememberUserDetails = (details = {}) => {
    try {
        if (!config.enabled || !details) return;
        rememberUser({ ...details, dob: toDdMmYyyy(details.dob) });
    } catch (e) {
        debugWarn('[MoEngage] rememberUserDetails failed', e);
    }
};

/**
 * Record the eSign vendor + transaction ref when eSign starts, so the
 * esign_* events on the return page can carry esign_provider / esign_txn_id.
 * @param {{ provider?: string, txnId?: string }} attempt
 * @returns {void}
 */
export const rememberEsignAttempt = (attempt = {}) => {
    try {
        if (!config.enabled || !attempt) return;
        rememberEsign(attempt);
    } catch (e) {
        debugWarn('[MoEngage] rememberEsignAttempt failed', e);
    }
};

/** @returns {void} */
export const logoutUser = () => {
    try {
        if (!config.enabled) return;
        lastIdentified = null;
        forgetUser();
        resetCommonProps();
        resetTrackOnce();
        resetDeferredEvents();
        debugInfo(config.mockMode ? '[MoEngage:mock] logoutUser' : '[MoEngage] logoutUser');
        enqueue((sdk) => sdk.logoutUser());
    } catch (e) {
        debugWarn('[MoEngage] logoutUser failed', e);
    }
};
